import React from "react"
import { Box, HStack, Text, VStack } from "@chakra-ui/react"
import { Link } from "../Link"
import { Tooltip } from "../../chakra/Tooltip"

export interface EntityPopoverItem {
  id: string
  name: string
  href?: string
  badge?: React.ReactNode
  subtitle?: string
  openExternal?: boolean
  prefix?: React.ReactNode
}

export interface EntityPopoverListProps {
  title?: string
  count?: number
  items?: EntityPopoverItem[]
  children?: React.ReactNode
  emptyText?: string
}

/**
 * Standardized list container for entity popovers (assets, orders, systems, attributes).
 * Provides consistent typography (13px), row padding, hover highlights, and persistent scrollbars.
 */
export const EntityPopoverList: React.FC<EntityPopoverListProps> = ({
  title,
  count,
  items,
  children,
  emptyText = "No items available",
}) => {
  return (
    <VStack align="stretch" gap="0.375em" width="100%" boxSizing="border-box" fontSize="1em">
      {title && (
        <HStack justify="space-between" px="0.25em" pb="0.125em">
          <Text fontWeight="600" fontSize="1em" color="primary">
            {title}
          </Text>
          {count !== undefined && (
            <Text fontSize="0.85em" color="gray.600" fontWeight="500">
              ({count})
            </Text>
          )}
        </HStack>
      )}

      {items && items.length > 0 ? (
        <VStack align="stretch" gap="0.375em" width="100%" boxSizing="border-box">
          {items.map((item) => (
            <HStack
              key={item.id}
              justify="space-between"
              align="center"
              gap="0.625em"
              py="0.375em"
              px="0.75em"
              width="100%"
              boxSizing="border-box"
              height="3rem"
              minHeight="3rem"
              maxHeight="3rem"
              bg="white"
              borderRadius="md"
              border="1px solid"
              borderColor="gray.200"
              boxShadow="0 1px 2px rgba(0, 0, 0, 0.04)"
              _hover={{ borderColor: "gray.300", bg: "#FAF9F8" }}
              transition="all 0.12s ease"
            >
              {item.prefix && (
                <Box flexShrink={0} display="inline-flex" alignItems="center">
                  {item.prefix}
                </Box>
              )}
              <Box flex="1" minWidth={0} overflow="hidden">
                <Tooltip content={item.name} disabled={!item.name || item.name.length < 25}>
                  <Box display="block" width="100%" minWidth={0}>
                    {item.href ? (
                      <Link
                        href={item.href}
                        variant="primary"
                        fontSize="1em"
                        target={item.openExternal ? "_blank" : undefined}
                        rel={item.openExternal ? "noopener noreferrer" : undefined}
                        display="block"
                        width="100%"
                        minWidth={0}
                      >
                        <Text
                          fontSize="1em"
                          fontWeight="500"
                          lineHeight="1.25"
                          whiteSpace="normal"
                          wordBreak="break-word"
                          style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {item.name}
                        </Text>
                      </Link>
                    ) : (
                      <Text
                        fontSize="1em"
                        fontWeight="500"
                        color="primary"
                        lineHeight="1.25"
                        whiteSpace="normal"
                        wordBreak="break-word"
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {item.name}
                      </Text>
                    )}
                  </Box>
                </Tooltip>
                {item.subtitle && (
                  <Text
                    fontSize="0.85em"
                    color="gray.500"
                    truncate
                    style={{
                      display: "-webkit-box",
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {item.subtitle}
                  </Text>
                )}
              </Box>
              {item.badge && (
                <Box flexShrink={0} display="inline-flex" alignItems="center">
                  {item.badge}
                </Box>
              )}
            </HStack>
          ))}
        </VStack>
      ) : children ? (
        children
      ) : (
        <Box
          py="0.75em"
          px="1em"
          bg="white"
          borderRadius="md"
          border="1px solid"
          borderColor="gray.200"
          textAlign="center"
          color="gray.500"
          fontSize="1em"
        >
          {emptyText}
        </Box>
      )}
    </VStack>
  )
}

export default EntityPopoverList
